import apiClient from './api';
import { API_ENDPOINTS } from '../config/api.config';
import { DID_CONFIG, DIDAvatar, DIDVoice, getDefaultAvatar, getVoiceForLanguage } from '../config/d-id.config';

export interface CreateAvatarSessionRequest {
  avatar_config: {
    source_url: string;
    avatar_id?: string;
    voice_id?: string;
    presenter_id?: string;
    driver_url?: string;
  };
  language?: string;
  client_info?: {
    user_agent?: string;
    platform?: string;
    browser?: string;
  };
}

export interface AvatarSessionResponse {
  session_id: string;
  d_id_stream_id: string;
  d_id_agent_id?: string;  // Optional - only for Agents sessions
  d_id_chat_id?: string;   // Optional - only for Agents sessions
  d_id_session_id?: string; // D-ID's internal session ID
  offer?: {  // Optional - should always be present for streaming
    type: string;
    sdp: string;
  };
  ice_servers: Array<{
    urls: string[];
    username?: string;
    credential?: string;
  }>;
  avatar_config: any;
}

export interface SDPAnswer {
  session_id: string;
  answer: {
    type: string;
    sdp: string;
  };
}

export interface ICECandidate {
  session_id: string;
  candidate: string;
  sdpMid: string;
  sdpMLineIndex: number;
}

export interface TalkMessage {
  session_id: string;
  message: string;
  language?: string;
}

class AvatarChatService {
  /**
   * Create a new avatar chat session with D-ID
   */
  async createSession(
    conversationId: string,
    avatar?: DIDAvatar,
    voice?: DIDVoice,
    language: string = 'en-US'
  ): Promise<AvatarSessionResponse> {
    try {
      // Use default avatar if not provided
      const selectedAvatar = avatar || getDefaultAvatar();
      
      // Get voice for language if not provided
      const selectedVoice = voice || getVoiceForLanguage(language);

      const request: CreateAvatarSessionRequest = {
        avatar_config: {
          source_url: selectedAvatar.source_url,
          avatar_id: selectedAvatar.id,
          voice_id: selectedVoice?.id,
          driver_url: DID_CONFIG.DEFAULT_DRIVER_URL
        },
        language,
        client_info: {
          user_agent: navigator.userAgent,
          platform: navigator.platform,
          browser: this.getBrowserInfo()
        }
      };

      const response = await apiClient.post(
        API_ENDPOINTS.AVATAR_CHAT.CREATE_SESSION,
        request
      );

      // Handle TypedResultEntity response format
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      console.error('Error creating avatar session:', error);
      throw new Error(error.response?.data?.message || 'Failed to create avatar session');
    }
  }

  /**
   * Submit SDP answer for WebRTC connection
   */
  async submitSDPAnswer(sessionId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    try {
      const request: SDPAnswer = {
        session_id: sessionId,
        answer: {
          type: answer.type || 'answer',
          sdp: answer.sdp || ''
        }
      };

      await apiClient.post(
        API_ENDPOINTS.AVATAR_CHAT.SUBMIT_SDP(sessionId),
        request
      );
    } catch (error: any) {
      console.error('Error submitting SDP answer:', error);
      throw new Error(error.response?.data?.message || 'Failed to submit SDP answer');
    }
  }

  /**
   * Submit ICE candidate for WebRTC connection
   */
  async submitICECandidate(sessionId: string, candidate: RTCIceCandidate): Promise<void> {
    try {
      const request: ICECandidate = {
        session_id: sessionId,
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid || '',
        sdpMLineIndex: candidate.sdpMLineIndex || 0
      };

      await apiClient.post(
        API_ENDPOINTS.AVATAR_CHAT.SUBMIT_ICE(sessionId),
        request
      );
    } catch (error: any) {
      // ICE candidate errors are non-fatal
      console.warn('Error submitting ICE candidate:', error);
    }
  }

  /**
   * Send text message for avatar to speak
   */
  async sendTalkMessage(
    sessionId: string,
    message: string,
    language?: string
  ): Promise<void> {
    try {
      const request: TalkMessage = {
        session_id: sessionId,
        message,
        language
      };

      await apiClient.post(
        API_ENDPOINTS.AVATAR_CHAT.SEND_TALK(sessionId),
        request
      );
    } catch (error: any) {
      console.error('Error sending talk message:', error);
      throw new Error(error.response?.data?.message || 'Failed to send talk message');
    }
  }

  /**
   * Send chat message with course context for avatar to speak
   */
  async sendChatWithContext(
    sessionId: string,
    message: string,
    courseId: string,
    conversationId: string,
    language?: string
  ): Promise<any> {
    try {
      const request = {
        session_id: conversationId,
        message,
        course_id: courseId,
        language: language || 'en-US'
      };

      const response = await apiClient.post(
        API_ENDPOINTS.AVATAR_CHAT.SEND_CHAT(sessionId),
        request
      );

      // Handle TypedResultEntity response format
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      console.error('Error sending chat with context:', error);
      throw new Error(error.response?.data?.message || 'Failed to send chat message');
    }
  }

  /**
   * Update connection state
   */
  async updateConnectionState(
    sessionId: string,
    connectionState: string,
    webrtcStats?: any
  ): Promise<void> {
    try {
      await apiClient.put(
        API_ENDPOINTS.AVATAR_CHAT.UPDATE_STATE(sessionId),
        {
          session_id: sessionId,
          connection_state: connectionState,
          webrtc_stats: webrtcStats
        }
      );
    } catch (error: any) {
      console.warn('Error updating connection state:', error);
    }
  }

  /**
   * End avatar session
   */
  async endSession(sessionId: string, reason?: string): Promise<void> {
    try {
      await apiClient.delete(
        API_ENDPOINTS.AVATAR_CHAT.END_SESSION(sessionId),
        { 
          data: { reason }
        }
      );
    } catch (error: any) {
      console.warn('Error ending session:', error);
    }
  }

  /**
   * Get session information
   */
  async getSessionInfo(sessionId: string): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.AVATAR_CHAT.GET_SESSION(sessionId)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error getting session info:', error);
      throw new Error(error.response?.data?.message || 'Failed to get session info');
    }
  }

  /**
   * Get session statistics
   */
  async getSessionStats(): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.AVATAR_CHAT.GET_STATS
      );
      return response.data;
    } catch (error: any) {
      console.error('Error getting session stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to get session stats');
    }
  }

  /**
   * Get available avatars
   */
  async getAvailableAvatars(): Promise<any[]> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.AVATAR_CHAT.GET_AVATARS
      );
      return response.data.avatars || [];
    } catch (error: any) {
      console.error('Error getting avatars:', error);
      return [];
    }
  }

  /**
   * Get available voices
   */
  async getAvailableVoices(language?: string): Promise<any[]> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.AVATAR_CHAT.GET_VOICES,
        { 
          params: language ? { language } : undefined
        }
      );
      return response.data.voices || [];
    } catch (error: any) {
      console.error('Error getting voices:', error);
      return [];
    }
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
    if (userAgent.indexOf('Safari') > -1) return 'Safari';
    if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
    if (userAgent.indexOf('Edge') > -1) return 'Edge';
    return 'Unknown';
  }
}

export default new AvatarChatService();